import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { portalApi } from '../api/portal'
import logoLigier from '../assets/logo_ligier.png'

// Paleta de marca Ligier — blanco y negro (mismo lenguaje que SeguimientoClientePage)
const INK = '#111111'      // negro de marca
const INK_SOFT = '#000000' // negro puro (header)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtFecha(f) {
  if (!f) return '—'
  const d = new Date(`${f}T00:00:00`)
  if (isNaN(d)) return f
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtImporte(n) {
  if (n == null) return '—'
  return Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 })
}

const ESTADO_ENVIO = {
  ingresado: { label: 'Ingresado', color: '#9ca3af' },
  confirmado: { label: 'En preparación', color: '#525252' },
  procesado: { label: 'En preparación', color: '#525252' },
  en_distribucion: { label: 'En camino', color: INK },
  entregado: { label: 'Entregado', color: '#166534' },
  no_entregado: { label: 'No entregado', color: '#991b1b' },
  reagendado: { label: 'Reprogramado', color: '#92400e' },
  anulado: { label: 'Anulado', color: '#991b1b' },
}

const ESTADO_RECIBO = {
  pendiente: 'En proceso',
  confirmado: 'En proceso',
  acreditado: 'Acreditado',
  devuelto: 'Devuelto',
}

const ESTADO_PRESUPUESTO = {
  enviado: 'Recibido',
  aceptado: 'Aceptado',
  convertido: 'Pedido en curso',
}

// ---------------------------------------------------------------------------
// /portal — solicitud de acceso (magic link)
// ---------------------------------------------------------------------------

function SolicitarAcceso() {
  const [email, setEmail] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [listo, setListo] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e) => {
    e.preventDefault(); setEnviando(true); setError('')
    try { await portalApi.solicitarAcceso(email); setListo(true) }
    catch (err) {
      setError(err.response?.status === 429
        ? 'Demasiados intentos. Esperá un minuto y volvé a probar.'
        : 'No pudimos procesar tu solicitud. Probá de nuevo.')
      setEnviando(false)
    }
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <img src={logoLigier} alt="Ligier" style={s.logo} />
        <h1 style={s.title}>Portal de clientes</h1>
        <p style={s.numero}>Tus envíos y comprobantes, siempre a mano</p>
      </div>
      <div style={s.body}>
        {listo ? (
          <div style={s.card}>
            <p style={{ textAlign: 'center', fontSize: '2.2rem', margin: '4px 0 10px' }}>📬</p>
            <p style={{ textAlign: 'center', fontWeight: 700, color: INK, margin: '0 0 6px' }}>Revisá tu correo</p>
            <p style={{ textAlign: 'center', color: '#555', fontSize: '0.93rem', margin: 0, lineHeight: 1.5 }}>
              Si tu email está registrado, te mandamos el link de acceso a tu portal.
              Puede tardar unos minutos — mirá también en spam.
            </p>
          </div>
        ) : (
          <div style={s.card}>
            <p style={{ margin: '0 0 14px', color: '#3b3b3b', fontSize: '0.95rem', lineHeight: 1.5 }}>
              Ingresá el email con el que comprás en Ligier y te mandamos un link de acceso personal. Sin contraseñas.
            </p>
            {error && <div style={s.err}>{error}</div>}
            <form onSubmit={submit}>
              <label style={s.lbl}>Tu email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                placeholder="nombre@email.com" style={s.inp} autoFocus />
              <button type="submit" disabled={enviando || !email} style={{ ...s.btnPrimary, width: '100%', marginTop: 14 }}>
                {enviando ? 'Enviando…' : 'Enviarme el link'}
              </button>
            </form>
          </div>
        )}
        <p style={s.legal}>
          Gracias por elegir Ligier. La venta de bebidas alcohólicas está prohibida a menores de 18 años (Ley 24.788).
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// /portal/:token — el portal
// ---------------------------------------------------------------------------

function FilaEnvio({ e }) {
  const nav = useNavigate()
  const est = ESTADO_ENVIO[e.estado] || { label: e.estado, color: '#525252' }
  return (
    <Link to={`/seguimiento/${e.token_seguimiento}`} style={s.fila}>
      {/* Foto que sacó el repartidor al entregar. Carga perezosa: la URL
          trae la imagen recién cuando la fila se dibuja. Tocando la fila se
          ve grande en la página de seguimiento. */}
      {e.foto_url && (
        <img
          src={e.foto_url}
          alt="Foto de la entrega"
          loading="lazy"
          onError={ev => { ev.currentTarget.style.display = 'none' }}
          style={s.fotoMini}
        />
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={s.filaTitulo}>{e.destinatario || 'Tu pedido'}</p>
        <p style={s.filaSub}>{e.tipo === 'NE' ? 'Envío' : 'Entrega'} {e.numero} · {fmtFecha(e.fecha)}</p>
        {/* Abre el reclamo del envío directo (form ya desplegado en el
            seguimiento). span con role=button: un <a> anidado es inválido. */}
        <span
          role="button"
          tabIndex={0}
          onClick={ev => { ev.preventDefault(); ev.stopPropagation(); nav(`/seguimiento/${e.token_seguimiento}?reclamo=1`) }}
          onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); nav(`/seguimiento/${e.token_seguimiento}?reclamo=1`) } }}
          style={s.reclamoLink}
        >
          ⚠️ Iniciar un reclamo
        </span>
      </div>
      <span style={{ ...s.estadoChip, color: est.color, borderColor: est.color }}>{est.label}</span>
    </Link>
  )
}

function Portal() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('envios') // 'envios' | 'comprobantes'

  const cargar = useCallback(async () => {
    try { setData((await portalApi.obtener(token)).data) }
    catch { setError('No encontramos tu portal. Pedí un link nuevo desde la página de acceso.') }
    finally { setLoading(false) }
  }, [token])

  useEffect(() => { cargar() }, [cargar])

  if (loading) return <div style={s.full}><p style={{ color: '#9ca3af' }}>Cargando tu portal…</p></div>
  if (error) return (
    <div style={s.full}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '2.5rem', marginBottom: 8 }}>🔑</p>
        <p style={{ color: INK, fontWeight: 700, marginBottom: 14 }}>{error}</p>
        <Link to="/portal" style={{ color: INK, fontWeight: 700 }}>Pedir acceso →</Link>
      </div>
    </div>
  )

  const c = data.comprobantes
  const totalComprobantes = c.facturas.length + c.recibos.length + c.presupuestos.length

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <img src={logoLigier} alt="Ligier" style={s.logo} />
        <h1 style={s.title}>Hola, {data.cliente.nombre}</h1>
        <p style={s.numero}>Cliente N° {data.cliente.codigo}</p>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        <button onClick={() => setTab('envios')} style={{ ...s.tab, ...(tab === 'envios' ? s.tabActiva : {}) }}>
          Envíos {data.envios.length > 0 && `(${data.envios.length})`}
        </button>
        <button onClick={() => setTab('comprobantes')} style={{ ...s.tab, ...(tab === 'comprobantes' ? s.tabActiva : {}) }}>
          Comprobantes {totalComprobantes > 0 && `(${totalComprobantes})`}
        </button>
      </div>

      <div style={s.body}>
        {tab === 'envios' && (
          <div style={s.card}>
            <p style={s.cardLbl}>Tus envíos</p>
            {data.envios.length === 0
              ? <p style={s.vacio}>Todavía no tenés envíos registrados.</p>
              : data.envios.map((e, i) => <FilaEnvio key={i} e={e} />)}
          </div>
        )}

        {tab === 'comprobantes' && (
          <>
            <div style={s.card}>
              <p style={s.cardLbl}>Facturas</p>
              {c.facturas.length === 0
                ? <p style={s.vacio}>Sin facturas por ahora.</p>
                : c.facturas.map((f, i) => (
                  <a key={i} href={f.pdf_url} target="_blank" rel="noreferrer" style={s.fila} title="Descargar factura en PDF">
                    <div style={{ minWidth: 0 }}>
                      <p style={s.filaTitulo}>Factura {f.numero}</p>
                      <p style={s.filaSub}>{fmtFecha(f.fecha)} · {fmtImporte(f.total)}</p>
                    </div>
                    <span style={{ ...s.estadoChip, color: INK, borderColor: '#d4d4d4' }}>⬇ PDF</span>
                  </a>
                ))}
            </div>

            <div style={s.card}>
              <p style={s.cardLbl}>Recibos</p>
              {c.recibos.length === 0
                ? <p style={s.vacio}>Sin recibos por ahora.</p>
                : c.recibos.map((r, i) => (
                  <a key={i} href={r.pdf_url} target="_blank" rel="noreferrer" style={s.fila} title="Descargar recibo en PDF">
                    <div style={{ minWidth: 0 }}>
                      <p style={s.filaTitulo}>{r.numero ? `Recibo ${r.numero}` : 'Pago registrado'}</p>
                      <p style={s.filaSub}>{fmtFecha(r.fecha)} · {ESTADO_RECIBO[r.estado] || r.estado} · {fmtImporte(r.importe)}</p>
                    </div>
                    <span style={{ ...s.estadoChip, color: INK, borderColor: '#d4d4d4' }}>⬇ PDF</span>
                  </a>
                ))}
            </div>

            {c.presupuestos.length > 0 && (
              <div style={s.card}>
                <p style={s.cardLbl}>Presupuestos</p>
                {c.presupuestos.map((p, i) => (
                  <a key={i} href={p.pdf_url} target="_blank" rel="noreferrer" style={s.fila}>
                    <div>
                      <p style={s.filaTitulo}>Presupuesto {p.numero}</p>
                      <p style={s.filaSub}>
                        {fmtFecha(p.fecha)} · {ESTADO_PRESUPUESTO[p.estado] || p.estado}
                        {p.valido_hasta && ` · válido hasta ${fmtFecha(p.valido_hasta)}`}
                      </p>
                    </div>
                    <span style={{ ...s.estadoChip, color: INK, borderColor: '#d4d4d4' }}>Ver PDF</span>
                  </a>
                ))}
              </div>
            )}
          </>
        )}

        <p style={s.legal}>
          Gracias por elegir Ligier. La venta de bebidas alcohólicas está prohibida a menores de 18 años (Ley 24.788).
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

export default function PortalClientePage() {
  const { token } = useParams()
  return token ? <Portal /> : <SolicitarAcceso />
}

// ---------------------------------------------------------------------------
// Estilos — mismos patrones que SeguimientoClientePage (marca B/N Ligier)
// ---------------------------------------------------------------------------

const s = {
  full: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', padding: 24 },
  wrap: { minHeight: '100vh', background: '#ffffff', maxWidth: 480, margin: '0 auto', paddingBottom: 32, fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' },

  header: { background: INK_SOFT, color: '#fff', padding: '24px 20px 28px', borderBottomLeftRadius: 22, borderBottomRightRadius: 22 },
  logo: { height: 76, width: 'auto', display: 'block', marginBottom: 16 },
  title: { margin: '0 0 2px', fontSize: '1.45rem', fontWeight: 800, lineHeight: 1.2 },
  numero: { margin: 0, fontSize: '0.9rem', opacity: 0.75 },

  tabs: { display: 'flex', gap: 8, padding: '14px 14px 0' },
  tab: { flex: 1, padding: '11px 0', borderRadius: 12, border: '1.5px solid #e5e5e5', background: '#f4f4f5', color: '#555', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer' },
  tabActiva: { background: INK, borderColor: INK, color: '#fff' },

  body: { padding: '12px 14px 0' },
  card: { background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: '1px solid #ececec', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  cardLbl: { margin: '0 0 8px', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase' },
  vacio: { margin: '4px 0', color: '#9ca3af', fontSize: '0.92rem' },

  fila: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '11px 12px', background: '#f4f4f5', borderRadius: 12, marginBottom: 8, textDecoration: 'none' },
  fotoMini: { width: 46, height: 46, objectFit: 'cover', borderRadius: 8, border: '1px solid #d4d4d4', flexShrink: 0, background: '#fff' },
  filaTitulo: { margin: 0, fontSize: '0.95rem', fontWeight: 700, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  filaSub: { margin: '2px 0 0', fontSize: '0.8rem', color: '#9ca3af' },
  reclamoLink: { display: 'inline-block', marginTop: 5, fontSize: '0.78rem', fontWeight: 700, color: '#525252', textDecoration: 'underline', cursor: 'pointer' },
  estadoChip: { flexShrink: 0, fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', border: '1.5px solid', borderRadius: 999, padding: '4px 10px', background: '#fff' },

  lbl: { display: 'block', fontSize: '0.88rem', fontWeight: 600, color: '#555', marginBottom: 6 },
  inp: { width: '100%', padding: '12px', borderRadius: 10, border: '1px solid #e0e0e0', fontSize: '1rem', boxSizing: 'border-box' },
  err: { background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: '0.9rem' },
  btnPrimary: { padding: '14px', borderRadius: 12, background: INK, color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },

  legal: { textAlign: 'center', fontSize: '0.72rem', color: '#9ca3af', margin: '18px 8px 0', lineHeight: 1.5 },
}
